import { useCallback } from "react";
import toast from "react-hot-toast";

import { useAuth } from "@/contexts/AuthContext";
import { claudeAgentApi } from "@/lib/api/claude-agent";
import { previewMessage } from "@/lib/visual-editor/post-message";
import { useSlideStore } from "@/stores/slide-store";
import type { AspectRatio } from "@/stores/slide-store";

interface UseSlideAspectRatioOptions {
  devContainerId?: string;
  previewIframeRef?: React.RefObject<HTMLIFrameElement | null>;
}

interface UseSlideAspectRatioResult {
  aspectRatio: AspectRatio;
  setAspectRatio: (next: AspectRatio) => Promise<void>;
}

// Single setter for `slides.json` aspectRatio. Optimistic store update +
// postMessage to the iframe for instant letterbox re-layout, then a PUT
// through `runMutation` so it serializes with reorder/duplicate writes (the
// sidecar would otherwise be free to interleave the two). On failure we roll
// the store back so the UI stays consistent with what the server actually
// persisted.
export function useSlideAspectRatio({
  devContainerId,
  previewIframeRef,
}: UseSlideAspectRatioOptions): UseSlideAspectRatioResult {
  const { getIdToken } = useAuth();
  const aspectRatio = useSlideStore(
    (s) => s.presentationConfig.aspectRatio,
  );

  const setAspectRatio = useCallback(
    async (next: AspectRatio) => {
      if (next !== "16:9" && next !== "4:3") return;
      if (!devContainerId) return;

      const prev = useSlideStore.getState().presentationConfig.aspectRatio;
      if (prev === next) return;

      useSlideStore.getState().setPresentationConfig({ aspectRatio: next });
      previewMessage.updatePresentationConfig(previewIframeRef?.current, {
        aspectRatio: next,
      });

      try {
        await useSlideStore.getState().runMutation(async () => {
          const token = await getIdToken();
          if (!token) throw new Error("No auth token");
          const fullConfig = useSlideStore.getState().presentationConfig;
          await claudeAgentApi.updateSlidesConfig(
            token,
            devContainerId,
            fullConfig,
          );
        });
      } catch (error) {
        console.error("Failed to update slide aspect ratio:", error);
        useSlideStore
          .getState()
          .setPresentationConfig({ aspectRatio: prev });
        previewMessage.updatePresentationConfig(previewIframeRef?.current, {
          aspectRatio: prev,
        });
        toast.error("Couldn't update aspect ratio. Please try again.");
      }
    },
    [devContainerId, getIdToken, previewIframeRef],
  );

  return { aspectRatio, setAspectRatio };
}
